import React, { useState, useEffect } from "react";
import { Plus, X, Star, Clock, Lock } from "lucide-react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  createPerformanceReview,
  fetchPerformanceReviewsByTeacher,
  updateAdminComments,
  updateObjectiveScore,
  completeReview,
  clearError,
  resetReviews,
} from "../../../redux/performanceReviewSlice";
import { fetchObjectivesByTeacher } from "../../../redux/annualPersonalObjectiveSlice";

const AdminPerformanceReview = ({ teacherEmail }) => {
  const dispatch = useDispatch();
  const { reviews, loading, error } = useSelector((state) => state.performanceReview);
  const { objectives } = useSelector((state) => state.annualPersonalObjective);

  const [activeReview, setActiveReview] = useState(null);
  const [commentText, setCommentText] = useState({});
  const [objectiveComments, setObjectiveComments] = useState({});
  const [objectiveScores, setObjectiveScores] = useState({});
  const [showYearModal, setShowYearModal] = useState(false);
  const [selectedReviewType, setSelectedReviewType] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [showCloseReviewModal, setShowCloseReviewModal] = useState(false);
  const [reviewToClose, setReviewToClose] = useState(null); // { id, type }

  // Fetch reviews and objectives when component mounts or teacherEmail changes
  useEffect(() => {
    if (teacherEmail) {
      dispatch(resetReviews());
      dispatch(fetchPerformanceReviewsByTeacher(teacherEmail));
      dispatch(fetchObjectivesByTeacher(teacherEmail));
    }
  }, [dispatch, teacherEmail]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Helper function to parse objectives if they come as JSON string
  const parseObjectives = (objectives) => {
    if (!objectives) return [];
    if (typeof objectives === "string") {
      try {
        return JSON.parse(objectives);
      } catch (e) {
        return [];
      }
    }
    return Array.isArray(objectives) ? objectives : [];
  };

  // Process reviews to ensure objectives are parsed correctly
  const processedReviews = (reviews || []).map((review) => ({
    ...review,
    objectives: parseObjectives(review.objectives),
  }));

  // Get available years from accepted objectives
  const getAvailableYears = () => {
    if (!objectives || !Array.isArray(objectives)) return [];
    const acceptedObjectives = objectives.filter((obj) => obj.status === "accepted");
    const years = [...new Set(acceptedObjectives.map((obj) => obj.year || obj.performanceYear))];
    return years.sort((a, b) => b - a); // Sort descending (newest first)
  };

  const handleInitiateReviewClick = (type) => {
    const availableYears = getAvailableYears();
    
    if (availableYears.length === 0) {
      toast.error("No accepted PDP objectives found. Employee must accept objectives first.");
      return;
    }

    setSelectedReviewType(type);
    setSelectedYear(null);
    setShowYearModal(true);
  };

  const handleInitiateReview = async () => {
    if (!teacherEmail) {
      toast.error("Teacher email is required");
      return;
    }

    if (!selectedYear) {
      toast.error("Please select a year");
      return;
    }

    // Only one final year review per year; multiple mid-term reviews allowed per year
    if (selectedReviewType === "finalYear") {
      const existingReview = processedReviews.find(
        (review) => review.type === "finalYear" && review.performanceYear === selectedYear
      );
      if (existingReview) {
        toast.error(
          `A final year review for ${selectedYear} already exists. You can only initiate one final year review per year.`
        );
        setShowYearModal(false);
        return;
      }
    }

    try {
      const reviewData = {
        teacherEmail: teacherEmail,
        type: selectedReviewType,
        performanceYear: selectedYear,
        initiatedDate: new Date().toISOString(),
      };
      await dispatch(createPerformanceReview(reviewData)).unwrap();
      toast.success(`${selectedReviewType === "midTerm" ? "Mid-term" : "Final Year"} review for ${selectedYear} initiated successfully`);
      setShowYearModal(false);
      setSelectedReviewType(null);
      setSelectedYear(null);
      // Refresh reviews
      dispatch(fetchPerformanceReviewsByTeacher(teacherEmail));
    } catch (err) {
      toast.error(err || "Failed to initiate review");
    }
  };

  const handleSaveAdminComment = async (reviewId) => {
    const comment = commentText[reviewId];
    // Get existing comment from review if textarea is empty
    const review = processedReviews.find((r) => r.id === reviewId);
    const commentToSave = comment?.trim() || review?.adminComments || "";
    
    if (!commentToSave) {
      toast.error("Please enter a comment");
      return;
    }
    try {
      await dispatch(
        updateAdminComments({
          reviewId,
          adminComments: commentToSave,
        })
      ).unwrap();
      // Clear the textarea after successful save
      setCommentText({ ...commentText, [reviewId]: "" });
      toast.success("Comment saved successfully");
      // Refresh reviews to get updated data
      await dispatch(fetchPerformanceReviewsByTeacher(teacherEmail)).unwrap();
    } catch (err) {
      toast.error(err || "Failed to save comment");
    }
  };

  const handleSaveObjectiveScore = async (reviewId, objectiveId, score, comment) => {
    if (!score) {
      toast.error("Please select a score");
      return;
    }
    try {
      await dispatch(
        updateObjectiveScore({
          reviewId,
          objectiveId,
          adminScore: score,
          adminComments: (comment || "").trim(),
        })
      ).unwrap();
      // Clear the form fields
      setObjectiveScores({ ...objectiveScores, [`${reviewId}-${objectiveId}`]: "" });
      setObjectiveComments({ ...objectiveComments, [`${reviewId}-${objectiveId}`]: "" });
      toast.success("Score and comment saved successfully");
      // Refresh reviews to get updated data
      await dispatch(fetchPerformanceReviewsByTeacher(teacherEmail)).unwrap();
    } catch (err) {
      toast.error(err || "Failed to save score");
    }
  };

  const handleOpenCloseReviewModal = (reviewId, type) => {
    setReviewToClose({ id: reviewId, type });
    setShowCloseReviewModal(true);
  };

  const handleCloseReviewModal = () => {
    setShowCloseReviewModal(false);
    setReviewToClose(null);
  };

  const handleCloseReviewConfirm = async () => {
    if (!reviewToClose) return;
    try {
      await dispatch(completeReview(reviewToClose.id)).unwrap();
      toast.success("Review closed successfully");
      handleCloseReviewModal();
      await dispatch(fetchPerformanceReviewsByTeacher(teacherEmail)).unwrap();
    } catch (err) {
      toast.error(err || "Failed to close review");
    }
  };

  const midTermReviews = processedReviews.filter((r) => r.type === "midTerm");
  const finalYearReviews = processedReviews.filter((r) => r.type === "finalYear");
  
  // Get available years from accepted objectives
  const availableYears = getAvailableYears();

  return (
    <div>
      {/* Header with Initiate Buttons */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="font-bold text-base text-blue-500">
          Performance Review ({processedReviews.length})
        </h1>
        <div className="flex gap-2">
          <button
            onClick={() => handleInitiateReviewClick("midTerm")}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 text-sm flex items-center gap-2"
            disabled={loading || availableYears.length === 0}
            title={
              availableYears.length === 0
                ? "No accepted PDP objectives found"
                : "Initiate mid-term review"
            }
          >
            <Plus size={16} />
            Initiate Mid-term
          </button>
          <button
            onClick={() => handleInitiateReviewClick("finalYear")}
            className="bg-purple-600 text-white px-3 py-1.5 rounded hover:bg-purple-700 text-sm flex items-center gap-2"
            disabled={loading || availableYears.length === 0}
            title={
              availableYears.length === 0
                ? "No accepted PDP objectives found"
                : "Initiate final year review"
            }
          >
            <Plus size={16} />
            Initiate Final Year
          </button>
        </div>
      </div>

      {loading && processedReviews.length === 0 && (
        <div className="text-center py-4 text-gray-500">
          <p>Loading reviews...</p>
        </div>
      )}

      {!loading && error && processedReviews.length === 0 && (
        <div className="text-center py-4 text-red-500">
          <p>Error loading reviews. Please try again.</p>
        </div>
      )}

      {/* Mid-term Reviews */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Clock className="text-blue-600" size={20} />
          Mid-term Reviews
        </h3>
        {midTermReviews.length === 0 ? (
          <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
            <p>No mid-term reviews initiated yet.</p>
            <p className="text-sm mt-1">Click "Initiate Mid-term" to start a review</p>
          </div>
        ) : (
          <div className="space-y-3">
            {midTermReviews.map((review) => (
              <div
                key={review.id}
                className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-700">
                        Year {review.performanceYear}
                      </span>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          review.status === "completed"
                            ? "bg-gray-200 text-gray-800"
                            : "bg-yellow-200 text-yellow-800"
                        }`}
                      >
                        {review.status === "completed" ? "Closed" : "In Progress"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Initiated: {new Date(review.initiatedDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {review.status !== "completed" && (
                      <button
                        onClick={() => handleOpenCloseReviewModal(review.id, "midTerm")}
                        className="flex items-center gap-1.5 bg-gray-600 text-white px-3 py-1.5 rounded hover:bg-gray-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading || !(review.employeeComments || "").trim()}
                        title={
                          (review.employeeComments || "").trim()
                            ? "Close this review. No further edits allowed."
                            : "Employee must submit comments before you can close this review."
                        }
                      >
                        <Lock size={14} />
                        Close Review
                      </button>
                    )}
                    {review.status !== "completed" && !(review.employeeComments || "").trim() && (
                      <span className="text-xs text-amber-600" title="Employee must submit comments before closing">
                        (Awaiting employee comments)
                      </span>
                    )}
                    <button
                      onClick={() =>
                        setActiveReview(activeReview === review.id ? null : review.id)
                      }
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      {activeReview === review.id ? "Hide Details" : "Show Details"}
                    </button>
                  </div>
                </div>

                {activeReview === review.id && (
                  <div className="space-y-4 mt-4 pt-4 border-t border-blue-200">
                    {/* Admin Comments Section */}
                    <div className="bg-white p-3 rounded border">
                      <h4 className="font-semibold text-gray-800 mb-2 text-sm">Your Comments</h4>
                      {review.adminComments ? (
                        <p className="text-sm text-gray-700 mb-2">{review.adminComments}</p>
                      ) : (
                        <p className="text-sm text-gray-500 italic mb-2">No comments yet</p>
                      )}
                      {review.status !== "completed" && (
                        <>
                          <textarea
                            value={commentText[review.id] !== undefined ? commentText[review.id] : (review.adminComments || "")}
                            onChange={(e) =>
                              setCommentText({ ...commentText, [review.id]: e.target.value })
                            }
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            rows="3"
                            placeholder={review.adminComments ? "Update your comments..." : "Add your comments on performance..."}
                          />
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => handleSaveAdminComment(review.id)}
                              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                              disabled={loading}
                            >
                              {review.adminComments ? "Update Comment" : "Save Comment"}
                            </button>
                            {commentText[review.id] && (
                              <button
                                onClick={() => setCommentText({ ...commentText, [review.id]: review.adminComments || "" })}
                                className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 text-sm"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                        </>
                      )}
                      {review.status === "completed" && (
                        <p className="text-xs text-gray-500 italic">This review is closed. No further edits.</p>
                      )}
                    </div>

                    {/* Employee Comments Section */}
                    <div className="bg-white p-3 rounded border">
                      <h4 className="font-semibold text-gray-800 mb-2 text-sm">Employee Comments</h4>
                      {review.employeeComments ? (
                        <p className="text-sm text-gray-700">{review.employeeComments}</p>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No comments from employee yet</p>
                      )}
                    </div>

                    {/* Objectives Review */}
                    <div className="bg-white p-3 rounded border">
                      <h4 className="font-semibold text-gray-800 mb-3 text-sm">Objectives Review</h4>
                      <div className="space-y-3">
                        {(review.objectives || []).map((obj) => (
                          <div key={obj.objectiveId} className="border-b pb-3 last:border-b-0">
                            <p className="font-medium text-sm text-gray-700 mb-2">{obj.objective}</p>
                            {obj.adminComments && (
                              <div className="mb-2">
                                <p className="text-xs font-semibold text-gray-600">Your Comments:</p>
                                <p className="text-sm text-gray-700">{obj.adminComments}</p>
                              </div>
                            )}
                            {obj.employeeComments && (
                              <div className="mb-2">
                                <p className="text-xs font-semibold text-gray-600">Employee Comments:</p>
                                <p className="text-sm text-gray-700">{obj.employeeComments}</p>
                              </div>
                            )}
                          </div>
                        ))}
                        {(!review.objectives || review.objectives.length === 0) && (
                          <p className="text-sm text-gray-500 italic">
                            No objectives available. Employee needs to accept PDP objectives first.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Final Year Reviews */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Star className="text-purple-600" size={20} />
          Final Year Reviews
        </h3>
        {finalYearReviews.length === 0 ? (
          <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
            <p>No final year reviews initiated yet.</p>
            <p className="text-sm mt-1">Click "Initiate Final Year" to start a review</p>
          </div>
        ) : (
          <div className="space-y-3">
            {finalYearReviews.map((review) => (
              <div
                key={review.id}
                className="border-2 border-purple-300 rounded-lg p-4 bg-purple-50"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-700">
                        Year {review.performanceYear}
                      </span>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          review.status === "completed"
                            ? "bg-gray-200 text-gray-800"
                            : "bg-yellow-200 text-yellow-800"
                        }`}
                      >
                        {review.status === "completed" ? "Closed" : "In Progress"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Initiated: {new Date(review.initiatedDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {review.status !== "completed" && (
                      <button
                        onClick={() => handleOpenCloseReviewModal(review.id, "finalYear")}
                        className="flex items-center gap-1.5 bg-gray-600 text-white px-3 py-1.5 rounded hover:bg-gray-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading || !(review.employeeComments || "").trim()}
                        title={
                          (review.employeeComments || "").trim()
                            ? "Close this review. No further edits allowed."
                            : "Employee must submit comments before you can close this review."
                        }
                      >
                        <Lock size={14} />
                        Close Review
                      </button>
                    )}
                    {review.status !== "completed" && !(review.employeeComments || "").trim() && (
                      <span className="text-xs text-amber-600" title="Employee must submit comments before closing">
                        (Awaiting employee comments)
                      </span>
                    )}
                    <button
                      onClick={() =>
                        setActiveReview(activeReview === review.id ? null : review.id)
                      }
                      className="text-purple-600 hover:text-purple-800 text-sm"
                    >
                      {activeReview === review.id ? "Hide Details" : "Show Details"}
                    </button>
                  </div>
                </div>

                {activeReview === review.id && (
                  <div className="space-y-4 mt-4 pt-4 border-t border-purple-200">
                    {/* Admin Comments Section */}
                    <div className="bg-white p-3 rounded border">
                      <h4 className="font-semibold text-gray-800 mb-2 text-sm">
                        Your Comments (After Discussion)
                      </h4>
                      {review.adminComments ? (
                        <p className="text-sm text-gray-700 mb-2">{review.adminComments}</p>
                      ) : (
                        <p className="text-sm text-gray-500 italic mb-2">No comments yet</p>
                      )}
                      {review.status !== "completed" && (
                        <>
                          <textarea
                            value={commentText[review.id] !== undefined ? commentText[review.id] : (review.adminComments || "")}
                            onChange={(e) =>
                              setCommentText({ ...commentText, [review.id]: e.target.value })
                            }
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            rows="3"
                            placeholder={review.adminComments ? "Update your comments..." : "Add your comments after discussion with employee..."}
                          />
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => handleSaveAdminComment(review.id)}
                              className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm"
                              disabled={loading}
                            >
                              {review.adminComments ? "Update Comment" : "Save Comment"}
                            </button>
                            {commentText[review.id] && (
                              <button
                                onClick={() => setCommentText({ ...commentText, [review.id]: review.adminComments || "" })}
                                className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 text-sm"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                        </>
                      )}
                      {review.status === "completed" && (
                        <p className="text-xs text-gray-500 italic">This review is closed. No further edits.</p>
                      )}
                    </div>

                    {/* Employee Comments Section */}
                    <div className="bg-white p-3 rounded border">
                      <h4 className="font-semibold text-gray-800 mb-2 text-sm">Employee Comments</h4>
                      {review.employeeComments ? (
                        <p className="text-sm text-gray-700">{review.employeeComments}</p>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No comments from employee yet</p>
                      )}
                    </div>

                    {/* Objectives with Scores */}
                    <div className="bg-white p-3 rounded border">
                      <h4 className="font-semibold text-gray-800 mb-3 text-sm">
                        Objectives Review & Scoring
                      </h4>
                      <div className="space-y-4">
                        {(review.objectives || []).map((obj) => (
                          <div
                            key={obj.objectiveId}
                            className="border-2 border-gray-200 rounded p-3"
                          >
                            <p className="font-medium text-sm text-gray-700 mb-3">{obj.objective}</p>

                            {review.status !== "completed" && (
                              <>
                                {/* Score Selection */}
                                <div className="mb-3">
                                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                                    Final Score (1-5)
                                  </label>
                                  <div className="flex gap-2">
                                    {[1, 2, 3, 4, 5].map((score) => (
                                      <button
                                        key={score}
                                        onClick={() =>
                                          setObjectiveScores({
                                            ...objectiveScores,
                                            [`${review.id}-${obj.objectiveId}`]: score,
                                          })
                                        }
                                        className={`w-10 h-10 rounded flex items-center justify-center text-sm ${
                                          objectiveScores[`${review.id}-${obj.objectiveId}`] === score ||
                                          obj.adminScore === score
                                            ? "bg-purple-600 text-white"
                                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        }`}
                                      >
                                        {score}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {/* Admin Comments for Objective */}
                                <div className="mb-3">
                                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                                    Your Comments
                                  </label>
                                  <textarea
                                    value={
                                      objectiveComments[`${review.id}-${obj.objectiveId}`] ||
                                      obj.adminComments ||
                                      ""
                                    }
                                    onChange={(e) =>
                                      setObjectiveComments({
                                        ...objectiveComments,
                                        [`${review.id}-${obj.objectiveId}`]: e.target.value,
                                      })
                                    }
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                    rows="2"
                                    placeholder="Add your comments and final score..."
                                  />
                                </div>

                                {/* Save Button */}
                                <button
                                  onClick={() =>
                                    handleSaveObjectiveScore(
                                      review.id,
                                      obj.objectiveId,
                                      objectiveScores[`${review.id}-${obj.objectiveId}`] ||
                                        obj.adminScore,
                                      objectiveComments[`${review.id}-${obj.objectiveId}`] ||
                                        obj.adminComments ||
                                        ""
                                    )
                                  }
                                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm"
                                  disabled={loading}
                                >
                                  Save Score & Comment
                                </button>
                              </>
                            )}

                            {/* Display Current Score */}
                            {obj.adminScore && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs font-semibold text-gray-600 mb-1">Current Score:</p>
                                <div className="flex gap-1 items-center">
                                  {[1, 2, 3, 4, 5].map((score) => (
                                    <Star
                                      key={score}
                                      size={18}
                                      className={
                                        score <= obj.adminScore
                                          ? "text-yellow-500 fill-yellow-500"
                                          : "text-gray-300"
                                      }
                                    />
                                  ))}
                                  <span className="ml-2 text-sm font-semibold">
                                    {obj.adminScore}/5
                                  </span>
                                </div>
                                {obj.adminComments && (
                                  <p className="text-sm text-gray-700 mt-2">{obj.adminComments}</p>
                                )}
                              </div>
                            )}

                            {/* Employee Comments */}
                            {obj.employeeComments && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs font-semibold text-gray-600 mb-1">
                                  Employee Comments:
                                </p>
                                <p className="text-sm text-gray-700">{obj.employeeComments}</p>
                              </div>
                            )}
                          </div>
                        ))}
                        {(!review.objectives || review.objectives.length === 0) && (
                          <p className="text-sm text-gray-500 italic">
                            No objectives available. Employee needs to accept PDP objectives first.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Close Review Confirmation Modal */}
      {showCloseReviewModal && reviewToClose && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-11/12 p-6 relative">
            <button
              onClick={handleCloseReviewModal}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
              aria-label="Close"
            >
              <X size={24} />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Lock size={24} className="text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  Close Review?
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {reviewToClose.type === "midTerm" ? "Mid-term" : "Annual"} performance review
                </p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to close this {reviewToClose.type === "midTerm" ? "mid-term" : "annual"} review? No further edits will be allowed once closed.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={handleCloseReviewModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseReviewConfirm}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Lock size={16} />
                {loading ? "Closing..." : "Close Review"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Year Selection Modal */}
      {showYearModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-11/12 p-6 relative">
            <button
              onClick={() => {
                setShowYearModal(false);
                setSelectedReviewType(null);
                setSelectedYear(null);
              }}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
            >
              <X size={24} />
            </button>
            <h2 className="text-xl font-bold text-[#241763] mb-4">
              Select Year for {selectedReviewType === "midTerm" ? "Mid-term" : "Final Year"} Review
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Performance Year <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedYear || ""}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
                >
                  <option value="">Select a year</option>
                  {availableYears.map((year) => {
                    // Only disable year for final year if a final year review already exists; mid-term allows multiple per year
                    const existingFinalYear = selectedReviewType === "finalYear" && processedReviews.find(
                      (review) => review.type === "finalYear" && review.performanceYear === year
                    );
                    return (
                      <option key={year} value={year} disabled={!!existingFinalYear}>
                        {year} {existingFinalYear ? "(Final year review already exists)" : ""}
                      </option>
                    );
                  })}
                </select>
                {selectedYear && (
                  <p className="text-xs text-gray-500 mt-2">
                    {selectedReviewType === "finalYear" &&
                    processedReviews.find(
                      (r) => r.type === "finalYear" && r.performanceYear === selectedYear
                    )
                      ? "⚠️ A final year review for this year already exists"
                      : `✓ Ready to create ${selectedReviewType === "midTerm" ? "mid-term" : "final year"} review for ${selectedYear}`}
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowYearModal(false);
                    setSelectedReviewType(null);
                    setSelectedYear(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInitiateReview}
                  disabled={!selectedYear || loading}
                  className="px-4 py-2 bg-[#241763] text-white rounded hover:bg-[#1a1247] disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? "Creating..." : "Initiate Review"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPerformanceReview;

